import React from 'react'
import { Alert, Box, Button, Chip, CircularProgress, Typography } from '@mui/material'

// "Judge-readiness" — a holistic, winning-bar check of a whole step against the
// questions science-fair judges commonly ask. Shared by Steps 3, 4 and 5; each
// step's edge function (step{N}-judge-readiness) returns the same shape.

export type JudgeCoverage = 'covered' | 'thin' | 'missing'
export type JudgeBand     = 'winning_ready' | 'almost' | 'some_gaps' | 'thin'
export interface JudgeReadinessResult {
  covered_count?: number
  total?:         number
  band:           JudgeBand
  summary?:       string
  questions?:     { question: string; coverage: JudgeCoverage; where?: string; improve?: string }[]
  ai_disclaimer?: string
}

const JUDGE_BAND: Record<JudgeBand, { label: string; color: 'success' | 'info' | 'warning' | 'error' }> = {
  winning_ready: { label: 'Winning-ready',  color: 'success' },
  almost:        { label: 'Almost there',   color: 'info' },
  some_gaps:     { label: 'Some gaps',       color: 'warning' },
  thin:          { label: 'Needs more',      color: 'error' },
}
const JUDGE_COVERAGE: Record<JudgeCoverage, { label: string; color: 'success' | 'warning' | 'error' }> = {
  covered: { label: 'Covered',   color: 'success' },
  thin:    { label: 'Thin',      color: 'warning' },
  missing: { label: 'Missing',   color: 'error' },
}

export default function JudgeReadinessPanel({ description, subject, canRun, loading, onRun, result, hint }: {
  /** The sentence under the heading — what this step's check looks at. */
  description: string
  /** Fills "Your ___ could answer N of M questions…", e.g. "background". */
  subject: string
  canRun: boolean; loading: boolean; onRun: () => void
  result: JudgeReadinessResult | null
  /** Shown under the button when the check can't run yet. */
  hint?: string | null
}) {
  return (
    <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        Aiming to win? Check your judge-readiness
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
        {description}
      </Typography>
      <Button variant="outlined" onClick={onRun} disabled={!canRun || loading}>
        {loading
          ? <><CircularProgress size={16} sx={{ mr: 1 }} />Checking…</>
          : 'Check judge-readiness'}
      </Button>
      {hint && !loading && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {hint}
        </Typography>
      )}
      {result && <JudgeReadinessResultView result={result} subject={subject} />}
    </Box>
  )
}

function JudgeReadinessResultView({ result, subject }: { result: JudgeReadinessResult; subject: string }) {
  const b = JUDGE_BAND[result.band] ?? JUDGE_BAND.some_gaps
  return (
    <Box sx={{ mt: 2.5 }}>
      <Alert severity={b.color} sx={{ mb: 2 }}>
        <strong>{b.label}.</strong>{' '}
        {typeof result.covered_count === 'number' && typeof result.total === 'number' && (
          <>Your {subject} could answer <strong>{result.covered_count} of {result.total}</strong> questions judges commonly ask. </>
        )}
        {result.summary}
      </Alert>

      {(result.questions ?? []).map((q, i) => {
        const c = JUDGE_COVERAGE[q.coverage] ?? JUDGE_COVERAGE.thin
        return (
          <Box key={i} sx={{ mb: 1.25, pl: 1.5, borderLeft: '3px solid', borderColor: `${c.color}.main` }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
              <Chip size="small" color={c.color} label={c.label} sx={{ mt: 0.25, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{q.question}</Typography>
            </Box>
            {q.coverage !== 'covered' && q.improve && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{q.improve}</Typography>
            )}
            {q.coverage === 'covered' && q.where && q.where !== 'none' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>from your {q.where}</Typography>
            )}
          </Box>
        )
      })}

      {result.ai_disclaimer && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, opacity: 0.85 }}>
          ⓘ {result.ai_disclaimer}
        </Typography>
      )}
    </Box>
  )
}
